/**
 * Sign out without silently throwing work away.
 *
 * Signing out wipes this device's copy, outbox included - shared phones must
 * not hand the next shift someone else's catalog. That made every sign-out
 * button a delete button for any change that had not reached the server, and
 * two of the three sign-out buttons did it without a word. Imported moulds that
 * the server had refused looked fine right up to the next sign-in.
 *
 * Now: try to send first. If everything goes, sign out. If anything is still
 * on the device, say how much and let the person decide.
 */
import { useState, type ReactNode } from 'react';
import { ConfirmDialog } from '../components/ui/dialog.js';
import { db } from '../db/schema.js';
import { syncNow } from '../sync/engine.js';
import { useAuth } from './AuthProvider.js';

async function unsentCount(): Promise<number> {
  const [edits, scans] = await Promise.all([
    db.outbox.where('status').anyOf('queued', 'sending', 'failed').count(),
    db.scanQueue.where('status').anyOf('queued', 'sending', 'failed').count(),
  ]);
  return edits + scans;
}

export function useSafeSignOut(): { request: () => void; busy: boolean; dialog: ReactNode } {
  const { signOut } = useAuth();
  const [unsent, setUnsent] = useState(0);
  const [busy, setBusy] = useState(false);

  async function request() {
    setBusy(true);
    try {
      if ((await unsentCount()) > 0) {
        // Bounded: offline, syncNow returns at once; online, a stuck request
        // must not trap the person on this screen.
        await Promise.race([syncNow(), new Promise((r) => setTimeout(r, 20_000))]);
      }
      const left = await unsentCount();
      if (left === 0) {
        await signOut();
        return;
      }
      setUnsent(left);
    } finally {
      setBusy(false);
    }
  }

  const dialog = (
    <ConfirmDialog
      open={unsent > 0}
      onOpenChange={(open) => {
        if (!open) setUnsent(0);
      }}
      title="Changes have not reached the server"
      description={`${unsent} change${unsent === 1 ? '' : 's'} on this device could not be sent - you may be offline, or the server refused ${unsent === 1 ? 'it' : 'some'} (see the sync status at the top). Signing out now deletes ${unsent === 1 ? 'it' : 'them'} for good.`}
      confirmLabel="Sign out and discard"
      destructive
      onConfirm={() => {
        setUnsent(0);
        void signOut();
      }}
    />
  );

  return { request: () => void request(), busy, dialog };
}
